/**
 * bgm.ts - Web Audio API BGM Playlist
 *
 * 3 tracks play in order, auto-advancing when each ends.
 * After all 3, loops back to first track.
 * Compatible with Toss WebView (no HTMLAudioElement).
 */

const PLAYLIST = [
  '/audio/bgm.mp3',
  '/audio/bgm-ghostbusters.mp3',
  '/audio/bgm-alt.mp3',
];

let audioContext: AudioContext | null = null;
let gainNode: GainNode | null = null;
let currentSource: AudioBufferSourceNode | null = null;
let currentTrackIndex = 0;
let isPlaying = false;
let isMuted = false;
let audioBuffers: (AudioBuffer | null)[] = [];
let initialized = false;

function getAudioContext(): AudioContext | null {
  if (audioContext) return audioContext;
  const AC = window.AudioContext || (window as any).webkitAudioContext;
  if (!AC) return null;
  audioContext = new AC();
  gainNode = audioContext.createGain();
  gainNode.gain.value = 0.4;
  gainNode.connect(audioContext.destination);
  return audioContext;
}

async function loadTrack(url: string): Promise<AudioBuffer | null> {
  const ctx = getAudioContext();
  if (!ctx) return null;
  try {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    return await ctx.decodeAudioData(arrayBuffer);
  } catch {
    return null;
  }
}

async function loadAllTracks(): Promise<void> {
  audioBuffers = await Promise.all(PLAYLIST.map(loadTrack));
}

function playTrack(index: number): void {
  const ctx = getAudioContext();
  if (!ctx || !gainNode) return;

  // Stop current
  if (currentSource) {
    try { currentSource.stop(); } catch { /* */ }
    currentSource = null;
  }

  const buffer = audioBuffers[index];
  if (!buffer) {
    // Find next track with a valid buffer
    for (let i = 1; i < PLAYLIST.length; i++) {
      const nextIdx = (index + i) % PLAYLIST.length;
      if (audioBuffers[nextIdx]) {
        currentTrackIndex = nextIdx;
        if (isPlaying) playTrack(currentTrackIndex);
        return;
      }
    }
    // No tracks loaded at all — stop trying
    return;
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(gainNode);

  // When track ends, play next
  source.onended = () => {
    if (!isPlaying) return;
    currentTrackIndex = (index + 1) % PLAYLIST.length;
    playTrack(currentTrackIndex);
  };

  source.start(0);
  currentSource = source;
  currentTrackIndex = index;
}

/** Initialize BGM system. Call once at startup. */
export async function initBgm(): Promise<void> {
  if (initialized) return;
  initialized = true;

  // Resume AudioContext on first user interaction
  const resume = async () => {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      await ctx.resume();
    }
    if (!isPlaying && !isMuted) {
      await startBgm();
    }
    document.removeEventListener('click', resume);
    document.removeEventListener('touchstart', resume);
  };
  document.addEventListener('click', resume, { once: true });
  document.addEventListener('touchstart', resume, { once: true });

  // Pre-load all tracks
  await loadAllTracks();
}

/** Start playing from current track */
async function startBgm(): Promise<void> {
  if (isPlaying) return;
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    await ctx.resume();
  }
  isPlaying = true;
  playTrack(currentTrackIndex);
}

/** Stop playing */
function stopBgm(): void {
  isPlaying = false;
  if (currentSource) {
    try { currentSource.stop(); } catch { /* */ }
    currentSource = null;
  }
}

/** Toggle BGM on/off. Returns new playing state. */
export function toggleBgm(): boolean {
  if (isMuted) {
    isMuted = false;
    startBgm();
    return true;
  } else {
    isMuted = true;
    stopBgm();
    return false;
  }
}

/** Check if BGM is currently playing */
export function isBgmOn(): boolean {
  return !isMuted;
}

/** Set BGM volume (0.0 ~ 1.0) */
export function setBgmVolume(vol: number): void {
  if (gainNode) {
    gainNode.gain.value = Math.max(0, Math.min(1, vol));
  }
}

/** Get current BGM volume (0.0 ~ 1.0) */
export function getBgmVolume(): number {
  return gainNode ? gainNode.gain.value : 0.4;
}
